<template>
  <div class="table-wrap" :style="scaleStyle">
    <my-loader v-if="isLoading && displayedOrders.length === 0" />
    <div v-else class="table-scroll">
      <table class="distr-table" v-if="displayedOrders.length > 0" :style="{ width: `${table.getTotalSize()}px` }">
        <thead>
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <th v-for="header in headerGroup.headers" :key="header.id" :style="{ width: `${header.getSize()}px` }">
              <button v-if="header.column.getCanSort()" class="th-sort" @click="sortBy(header.column.id, $event)">
                {{ header.column.columnDef.header }} {{ sortLabel(header.column.id) }}
              </button>
              <span v-else>{{ header.column.columnDef.header }}</span>
              <span
                class="column-resizer"
                :class="{ resizing: header.column.getIsResizing() }"
                title="Изменить ширину столбца"
                aria-hidden="true"
                @mousedown.stop="header.getResizeHandler()($event)"
                @touchstart.stop="header.getResizeHandler()($event)"
                @click.stop
                @dblclick.stop="header.column.resetSize()"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, idx) in table.getRowModel().rows" :key="row.id">
            <tr :class="rowClasses(row.original, idx)" @click="toggleExpand(row.id)">
              <td v-for="cell in row.getVisibleCells()" :key="cell.id" :class="{ 'critical-cell': cell.column.id === 'critical' }" :style="{ width: `${cell.column.getSize()}px` }">
                <template v-if="cell.column.id === 'critical'"><span v-if="row.original.is_critical_closing" class="critical-badge">!</span></template>
                <template v-else-if="cell.column.id === 'status'">
                  <span :class="['status-badge', statusClass(row.original)]">{{ statusText(row.original) }}</span>
                </template>
                <template v-else-if="cell.column.id === 'distr_id'">
                  <span class="id-cell">{{ row.original.distr_id }}</span>
                  <span v-if="row.original.manual_status_override" class="manual-status-flag" title="Ручной статус — автосинхронизация отключена">🧭</span>
                </template>
                <template v-else-if="cell.column.id === 'departure.planed_date'"><span :class="{ overdue: row.original.is_overdue }">{{ formatDate(getLoadingPoint(row.original)?.planed_date) }}</span></template>
                <template v-else-if="cell.column.id === 'closing_urgency'">
                  <div
                    v-if="timelineModel(row.original)"
                    class="tl-widget"
                    :title="timelineModel(row.original).tooltip"
                  >
                    <div class="tl-track">
                      <div
                        class="tl-bar"
                        :class="timelineModel(row.original).barCls"
                        :style="{ width: timelineModel(row.original).barPct + '%' }"
                      ></div>
                    </div>
                    <div
                      v-if="timelineModel(row.original).markPct != null"
                      class="tl-mark"
                      :style="{ left: timelineModel(row.original).markPct + '%' }"
                    ></div>
                    <div
                      v-if="timelineModel(row.original).mark12Pct != null"
                      class="tl-mark tl-mark-12"
                      :style="{ left: timelineModel(row.original).mark12Pct + '%' }"
                    ></div>
                  </div>
                  <span v-else>—</span>
                </template>
                <template v-else-if="cell.column.id === 'departure.city_name'">
                  <span :title="cityName(getLoadingPoint(row.original))">{{ cityName(getLoadingPoint(row.original)) }}</span>
                  <span v-if="extraLoading(row.original) > 0" class="extra-count"> ({{ extraLoading(row.original) }})</span>
                </template>
                <template v-else-if="cell.column.id === 'destination.city_name'">
                  <span :title="cityName(getDestPoint(row.original))">{{ cityName(getDestPoint(row.original)) }}</span>
                  <span v-if="extraUnloading(row.original) > 0" class="extra-count"> ({{ extraUnloading(row.original) }})</span>
                </template>
                <template v-else-if="cell.column.id === 'cargo'"><span :title="cargoList(row.original)">{{ cargoList(row.original) }}</span></template>
                <template v-else-if="cell.column.id === 'order.veh.type'">{{ row.original.order?.veh?.type || '—' }}</template>
                <template v-else-if="cell.column.id === 'tariff'">
                  <div class="tariff-cell" :title="tariffTitle(row.original)">
                    <span :class="['tariff-type', tariffClass(row.original)]">{{ tariffShort(row.original) }}</span>
                    <span class="tariff-value">{{ tariffValue(row.original) }}</span>
                  </div>
                </template>
                <template v-else-if="cell.column.id === 'payment'">{{ paymentTerm(row.original) }}</template>
                <template v-else-if="cell.column.id === 'display_name'">{{ row.original.display_name || row.original.order?.meta?.service_name || '—' }}</template>
                <template v-else-if="cell.column.id === 'client_category'">
                  <span
                    v-if="clientCategoryBadge(row.original)"
                    :class="clientCategoryBadge(row.original).cls"
                    :title="`Категория клиента: ${clientCategoryBadge(row.original).title}`"
                  >{{ clientCategoryBadge(row.original).label }}</span>
                  <span v-else :title="clientCategoryMeta(row.original) ? `Категория клиента: ${clientCategoryMeta(row.original).label}` : null">—</span>
                </template>
                <template v-else-if="cell.column.id === 'order.meta.client_name'">
                  {{ row.original.order?.meta?.client_name || '—' }}
                </template>
                <template v-else-if="cell.column.id === 'worker'">
                  <div class="actor-cell">
                    <span v-if="hasExecutor(row.original)" class="exec-line">
                      <span
                        v-if="executorRole(row.original)"
                        class="exec-icon"
                        :class="{ 'exec-icon-reserved': executorRole(row.original).key === 'reserved' }"
                        role="img"
                        :aria-label="executorRole(row.original).key"
                        :title="executorRole(row.original).tooltip"
                      >{{ executorRole(row.original).icon }}</span>{{ executorName(row.original) }}<span v-if="expirationDate(row.original)" class="exec-date">, {{ expirationDate(row.original) }}</span>
                    </span>
                    <span
                      v-else-if="executorRole(row.original)"
                      class="exec-icon exec-icon-standalone"
                      :class="{ 'exec-icon-reserved': executorRole(row.original).key === 'reserved' }"
                      role="img"
                      :aria-label="executorRole(row.original).key"
                      :title="executorRole(row.original).tooltip"
                    >{{ executorRole(row.original).icon }}</span>
                    <span v-if="queueBadge(row.original).length" class="queue-badge">{{ queueBadge(row.original).join(', ') }}</span>
                  </div>
                </template>
                <template v-else-if="cell.column.id === 'actions'">
                  <div class="actions">
                    <button class="act-btn" :disabled="isPlaceholderData" @click.stop="copyOrder(row.original)" title="Копировать данные">copy</button>
                    <button class="act-btn" :disabled="isPlaceholderData" @click.stop="copyNumber(row.original.distr_id)" title="Копировать номер">#</button>
                  </div>
                </template>
              </td>
            </tr>
            <tr v-show="expanded === row.id && !isPlaceholderData" class="detail-row">
              <td :colspan="row.getVisibleCells().length" :class="detailHighlightClass(idx)">
                <DistributionDetails
                  :order="row.original"
                  :detailActive="expanded === row.id && !isPlaceholderData"
                  :currentUser="currentUser"
                  :permissions="permissions"
                  :operationLoadingUid="operationLoadingUid"
                  :runOperationMutation="runOperation"
                  @baseRateSaved="$emit('baseRateSaved')"
                  @advanceCompleted="$emit('advanceCompleted')"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-if="isRefreshing && displayedOrders.length > 0" class="refresh-bar" />

      <div v-if="displayedOrders.length === 0 && !isLoading" class="empty-table">
        Нет заказов по заданным фильтрам
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { functionalUpdate, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import { createToaster } from '@meforma/vue-toaster';
import DistributionDetails from './DistributionDetails.vue';
import { tableScaleStyle } from '@/components/UI/tableScale.mjs';
import { closeTimelineModel as timelineModel } from '../closeTimeline';
import {
  EMPTY_VALUE,
  formatCargo,
  formatDate,
  formatMoney,
  formatOrderSummary,
  getDestinationPoint,
  getExecutor,
  getExecutorRoleMeta,
  getExtraLoadingCount,
  getExtraUnloadingCount,
  getHighlightIndexes,
  getClientCategoryMeta,
  getClosingStatusKey,
  getLoadingPoint,
  getPaymentType,
  getPaymentTerm,
  getStatusMeta,
  getTariffMeta,
  sortMeta,
  titleCase,
} from '../helpers';

export default {
  name: 'DistributionTable',
  components: { DistributionDetails },
  props: {
    orders: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    isRefreshing: { type: Boolean, default: false },
    isPlaceholderData: { type: Boolean, default: false },
    palette: { type: String, default: 'colorful' },
    highlightMode: { type: String, default: 'none' },
    autoExpandSingle: { type: Boolean, default: false },
    sorting: { type: Array, default: () => [] },
    columnOrder: { type: Array, default: () => [] },
    columnVisibility: { type: Object, default: () => ({}) },
    columnSizing: { type: Object, default: () => ({}) },
    pageCount: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
    currentUser: { type: Object, default: () => ({}) },
    permissions: { type: Object, default: () => ({}) },
    operationLoadingUid: { type: String, default: null },
    runOperation: { type: Function, required: true },
    scale: { type: String, default: 'normal' },
  },
  emits: ['sortingChanged', 'columnSizingChanged', 'baseRateSaved', 'advanceCompleted'],
  setup(props, { emit }) {
    const expanded = ref(null);
    const displayedOrders = computed(() => (Array.isArray(props.orders) ? props.orders : []));
    const scaleStyle = computed(() => tableScaleStyle(props.scale));
    const columns = [
      { id: 'critical', header: '!', size: 18, minSize: 18, maxSize: 40, enableSorting: false },
      { id: 'status', header: 'Статус', size: 70, minSize: 60, maxSize: 130, enableSorting: false },
      { id: 'distr_id', header: 'Номер', size: 75, minSize: 60, maxSize: 150, enableSorting: false },
      { id: 'departure.planed_date', header: 'Погрузка', accessorFn: (row) => getLoadingPoint(row)?.planed_date, size: 85, minSize: 75, maxSize: 180 },
      { id: 'closing_urgency', header: 'Закрытие', accessorFn: () => '', size: 120, minSize: 90, maxSize: 180 },
      { id: 'departure.city_name', header: 'Откуда', accessorFn: (row) => getLoadingPoint(row)?.city_name, size: 138, minSize: 90, maxSize: 350 },
      { id: 'destination.city_name', header: 'Куда', accessorFn: (row) => getDestinationPoint(row)?.city_name, size: 138, minSize: 90, maxSize: 350 },
      { id: 'cargo', header: 'Груз', size: 120, minSize: 80, maxSize: 350, enableSorting: false },
      { id: 'order.veh.type', header: 'ТС', accessorFn: (row) => row.order?.veh?.type, size: 50, minSize: 50, maxSize: 130 },
      { id: 'tariff', header: 'Тариф', size: 115, minSize: 95, maxSize: 220, enableSorting: false },
      { id: 'payment', header: 'Оплата', size: 70, minSize: 65, maxSize: 200, enableSorting: false },
      { id: 'display_name', header: 'Платформа', accessorFn: (row) => row.display_name, size: 110, minSize: 85, maxSize: 240 },
      { id: 'client_category', header: 'ТОП/КА', accessorFn: (row) => row.client_category, size: 62, minSize: 50, maxSize: 120, enableSorting: false },
      { id: 'order.meta.client_name', header: 'Клиент', accessorFn: (row) => row.order?.meta?.client_name, size: 90, minSize: 70, maxSize: 200, enableSorting: false },
      { id: 'worker', header: 'Исполнитель', accessorFn: (row) => row.worker, size: 150, minSize: 100, maxSize: 300 },
      { id: 'actions', header: 'Действия', size: 90, minSize: 80, maxSize: 150, enableSorting: false },
    ];
    const table = useVueTable({
      get data() { return displayedOrders.value; },
      columns,
      getCoreRowModel: getCoreRowModel(),
      getRowId: (row, index) => row._id || `${row.order?.meta?.id}-${row.distr_id || 'no-distr'}-${index}`,
      manualSorting: true,
      manualPagination: true,
      get pageCount() { return props.pageCount; },
      columnResizeMode: 'onChange',
      state: {
        get sorting() { return props.sorting; },
        get columnOrder() { return props.columnOrder; },
        get columnVisibility() { return props.columnVisibility; },
        get columnSizing() { return props.columnSizing; },
        get pagination() { return { pageIndex: Math.max(0, props.currentPage - 1), pageSize: 100 }; },
      },
      onSortingChange: (updater) => emit('sortingChanged', functionalUpdate(updater, props.sorting)),
      onColumnSizingChange: (updater) => emit('columnSizingChanged', functionalUpdate(updater, props.columnSizing)),
    });

    const rowKey = (order, index) => order._id || `${order.order?.meta?.id}-${order.distr_id || 'no-distr'}-${index}`;
    const toggleExpand = (key) => {
      if (!props.isPlaceholderData) expanded.value = expanded.value === key ? null : key;
    };
    watch(
      () => [props.autoExpandSingle, displayedOrders.value.length, displayedOrders.value[0]?._id, displayedOrders.value[0]?.distr_id],
      () => {
        if (props.autoExpandSingle && displayedOrders.value.length === 1) {
          expanded.value = rowKey(displayedOrders.value[0], 0);
        }
      },
      { immediate: true }
    );
    const cityName = (point) => titleCase(point?.city_name);
    const cargoList = (order) => formatCargo(order);
    const statusText = (order) => getStatusMeta(order).label;
    const statusClass = (order) => `status-${getStatusMeta(order).key}`;
    const clientCategoryMeta = (order) => getClientCategoryMeta(order);
    const clientCategoryBadge = (order) => {
      const meta = getClientCategoryMeta(order);
      if (!meta) return null;
      if (meta.key === 'top') return { cls: 'client-top-badge', label: 'ТОП', title: meta.label };
      if (meta.key === 'key') return { cls: 'client-key-badge', label: 'КА', title: meta.label };
      return null;
    };
    const extraLoading = (order) => getExtraLoadingCount(order);
    const extraUnloading = (order) => getExtraUnloadingCount(order);
    const executorName = (order) => getExecutor(order)?.first_name || null;
    const hasExecutor = (order) => Boolean(getExecutor(order)?.first_name);
    const executorRole = (order) => getExecutorRoleMeta(order);
    const expirationDate = (order) => {
      const executor = getExecutor(order);
      return executor?.exiration_dt ? formatDate(executor.exiration_dt) : null;
    };
    const queueBadge = (order) => order.pending_queue?.map((user) => user.first_name).filter(Boolean) || [];
    const paymentType = (order) => getPaymentType(order);
    const paymentTerm = (order) => getPaymentTerm(order);
    const tariffValue = (order) => {
      const tariff = getTariffMeta(order);
      if (tariff.left == null && tariff.right == null) return EMPTY_VALUE;
      return `${formatMoney(tariff.left)} / ${formatMoney(tariff.right)}`;
    };
    const tariffShort = (order) => getTariffMeta(order).shortType;
    const tariffTitle = (order) => getTariffMeta(order).type;
    const tariffClass = (order) => {
      const type = getTariffMeta(order).type;
      if (type === 'Согласованный') return 'tariff-approved';
      if (type === 'Ручной') return 'tariff-manual';
      if (type === 'Базовый') return 'tariff-base';
      return 'tariff-empty';
    };
    const toaster = createToaster();
    const copyOrder = (order) => navigator.clipboard?.writeText(formatOrderSummary(order)).then(() => { toaster.success('Скопировано'); }).catch(() => {});
    const copyNumber = (id) => navigator.clipboard?.writeText(String(id || '')).then(() => { toaster.success('Скопировано'); }).catch(() => {});

    const clientCategoryRowClass = (order) => {
      const meta = getClientCategoryMeta(order);
      if (!meta) return null;
      return `row-cat-${meta.key}`;
    };

    const highlightIndexes = computed(() => getHighlightIndexes(displayedOrders.value, props.highlightMode));
    const rowClasses = (order, index) => {
      const classes = ['data-row'];
      const highlightIndex = highlightIndexes.value[index];
      if (props.highlightMode === 'urgency') {
        if (order.is_critical_closing) classes.push('row-urgency');
      } else if (props.highlightMode === 'status') {
        classes.push(`row-status-${getStatusMeta(order).key}`);
      } else if (props.highlightMode === 'tariff') {
        classes.push(`row-${tariffClass(order)}`);
      } else if (props.highlightMode === 'clientCategory') {
        const catClass = clientCategoryRowClass(order);
        if (catClass) classes.push(catClass);
      } else if (props.highlightMode === 'closingStatus') {
        classes.push(`row-close-${getClosingStatusKey(order)}`);
      } else if (highlightIndex !== null) {
        classes.push(props.palette === 'zebra' ? `hl-zebra-${highlightIndex % 2}` : props.palette === 'bee' ? `hl-bee-${highlightIndex % 2}` : `hl-${highlightIndex % 12}`);
      }
      if (order.is_overdue) classes.push('row-overdue');
      if (order.is_critical_closing) classes.push('row-critical');
      return classes;
    };
    const detailHighlightClass = (index) => {
      const order = displayedOrders.value[index];
      if (props.highlightMode === 'urgency') {
        return order?.is_critical_closing ? 'row-urgency' : '';
      }
      if (props.highlightMode === 'status') {
        return `row-status-${getStatusMeta(order).key}`;
      }
      const highlightIndex = highlightIndexes.value[index];
      if (props.highlightMode === 'tariff') return `row-${tariffClass(order)}`;
      if (props.highlightMode === 'clientCategory') return clientCategoryRowClass(order) || '';
      if (props.highlightMode === 'closingStatus') return `row-close-${getClosingStatusKey(order)}`;
      if (highlightIndex === null) return '';
      return props.palette === 'zebra' ? `hl-zebra-${highlightIndex % 2}` : props.palette === 'bee' ? `hl-bee-${highlightIndex % 2}` : `hl-${highlightIndex % 12}`;
    };
    const sortBy = (field, event) => {
      const current = props.sorting;
      const index = current.findIndex((item) => item.id === field);
      if (!event.shiftKey || field === 'worker') {
        emit('sortingChanged', [{ id: field, desc: index === 0 ? !current[0].desc : false }]);
        return;
      }
      emit('sortingChanged', index < 0
        ? [...current, { id: field, desc: false }]
        : current.map((item, itemIndex) => itemIndex === index ? { ...item, desc: !item.desc } : item));
    };
    const sortLabel = (field) => {
      const sorts = props.sorting.map((item) => ({ field: item.id, dir: item.desc ? -1 : 1 }));
      const direct = sortMeta(sorts, field);
      return !direct.active && field === 'departure.planed_date'
        ? sortMeta(sorts, 'departure.planed_date_day').label
        : direct.label;
    };

    return {
      expanded,
      displayedOrders,
      scaleStyle,
      table,
      rowKey,
      toggleExpand,
      timelineModel,
      formatDate,
      getLoadingPoint,
      getDestPoint: getDestinationPoint,
      cityName,
      cargoList,
      statusText,
      statusClass,
      clientCategoryMeta,
      clientCategoryBadge,
      extraLoading,
      extraUnloading,
      executorName,
      hasExecutor,
      executorRole,
      expirationDate,
      queueBadge,
      paymentType,
      paymentTerm,
      tariffValue,
      tariffShort,
      tariffTitle,
      tariffClass,
      copyOrder,
      copyNumber,
      rowClasses,
      detailHighlightClass,
      sortBy,
      sortLabel,
    };
  },
};
</script>

<style scoped>
.table-wrap {
  position: relative;
  font-size: 13px;
  color: #333;
}
.table-scroll { overflow-x: auto; }

.distr-table {
  width: 100%;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.distr-table th,
.distr-table td {
  box-sizing: border-box;
  text-align: left;
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
  border-bottom: 1px solid #edf0f3;
  font-size: var(--table-cell-font-size);
  height: var(--table-cell-height);
  line-height: var(--table-cell-line-height);
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.distr-table th {
  position: relative;
  font-weight: 700;
  background: #f7f8fa;
  color: #444;
}
.column-resizer {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  width: 10px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
}
.column-resizer::before {
  content: '';
  position: absolute;
  top: 4px;
  right: 4px;
  bottom: 4px;
  width: 1px;
  background: #aeb8c5;
  transition: background-color 120ms ease;
}
.column-resizer:hover::before,
.column-resizer.resizing::before {
  background: #007bff;
}
.th-sort {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.th-sort:hover { color: #007bff; }
.data-row { cursor: pointer; user-select: none; }
.data-row:hover { background: #f3f6fb; }
.row-overdue { box-shadow: inset 2px 0 0 #dc3545; }
.row-critical { box-shadow: inset 2px 0 0 #b83235; }
.critical-cell { padding-left: 4px !important; padding-right: 2px !important; }
.critical-badge { color: #b83235; font-weight: 700; }

.status-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}
.status-opened { background: #007bff; }
.status-closed { background: #28a745; }
.status-canceled { background: #dc3545; }
.status-deleted { background: #6c757d; }
.status-outdated { background: #a87836; }
.status-breakdown { background: #fd7e14; }
.status-refusal { background: #6f42c1; }
.status-unknown { background: #adb5bd; }

.id-cell { font-weight: 600; }
.manual-status-flag { margin-left: 4px; cursor: help; }
.overdue { color: #dc3545; font-weight: 600; }
.extra-count { color: #888; font-size: 12px; }

.client-top-badge,
.client-key-badge {
  flex-shrink: 0;
  display: inline-block;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
}

.client-top-badge { background: #fff3cd; color: #856404; }
.client-key-badge { background: #cce5ff; color: #004085; }

.tariff-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.tariff-type {
  flex-shrink: 0;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
}

.tariff-approved { background: #d4edda; color: #155724; }
.tariff-manual { background: #fff3cd; color: #856404; }
.tariff-base { background: #cce5ff; color: #004085; }
.tariff-empty { background: #e5e7eb; color: #666; }
.tariff-value { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.payment-type { color: #777; font-size: 12px; }

.actor-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.exec-line {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.exec-icon { margin-right: 6px; }
.exec-icon-reserved { margin-left: 6px; }
.exec-icon-standalone { margin-right: 0; }
.exec-date { color: #3f50b5; font-weight: 600; }

.queue-badge { color: #888; font-size: 12px; }

.actions { display: flex; gap: 4px; }
.act-btn {
  display: inline-block;
  padding: 2px 5px;
  border: 1px solid #d7dce2;
  border-radius: 3px;
  background: #fff;
  font-size: 11px;
  cursor: pointer;
  color: #333;
  text-decoration: none;
}
.act-btn:hover { border-color: #007bff; color: #007bff; }

.detail-row,
.detail-row * { user-select: text; }
.detail-row td {
  padding: 0;
  border-bottom: 1px solid #edf0f3;
  white-space: normal;
  overflow: visible;
}
.state-cell,
.empty-table {
  text-align: left;
  padding: 28px;
  color: #999;
  font-size: 14px;
}

.hl-0 { background: #eef6ff; --details-card-bg: #d9ebff; }
.hl-1 { background: #fff3f7; --details-card-bg: #f8dce7; }
.hl-2 { background: #effaf1; --details-card-bg: #d9f0de; }
.hl-3 { background: #fff7eb; --details-card-bg: #f6e4c8; }
.hl-4 { background: #f7f0fb; --details-card-bg: #eadcf3; }
.hl-5 { background: #edfafd; --details-card-bg: #d7edf2; }
.hl-6 { background: #fff0ec; --details-card-bg: #f3d9d1; }
.hl-7 { background: #f0f2ff; --details-card-bg: #dde2fb; }
.hl-8 { background: #fffbe8; --details-card-bg: #f2e8bd; }
.hl-9 { background: #f4f0ee; --details-card-bg: #e4dbd6; }
.hl-10 { background: #f5faed; --details-card-bg: #e1edcf; }
.hl-11 { background: #f3f0fb; --details-card-bg: #e3dcf3; }
.hl-zebra-0 { background: #fff; --details-card-bg: #edf0f3; }
.hl-zebra-1 { background: #f7f8fa; --details-card-bg: #e8ebef; }
.hl-bee-0 { background: #fff; --details-card-bg: #edf0f3; }
.hl-bee-1 { background: #fff8df; --details-card-bg: #f0e2a8; }
.row-tariff-approved { background: #d4edda; --details-card-bg: #bfe2c8; }
.row-tariff-manual { background: #fff3cd; --details-card-bg: #ead99c; }
.row-tariff-base { background: #cce5ff; --details-card-bg: #b4d4f5; }
.row-tariff-empty { background: #f1f3f5; --details-card-bg: #e0e4e8; }
.row-urgency { background: #ffe8e8; --details-card-bg: #f8d5d5; }
.row-status-opened { background: #e8f0fe; --details-card-bg: #d0e1fd; }
.row-status-closed { background: #d4edda; --details-card-bg: #bfe2c8; }
.row-status-canceled { background: #ffe8e8; --details-card-bg: #f8d5d5; }
.row-status-deleted { background: #e8e8e8; --details-card-bg: #d6d6d6; }
.row-status-outdated { background: #efe6dc; --details-card-bg: #e2d4c2; }
.row-status-breakdown { background: #ffe4b5; --details-card-bg: #f5d9a0; }
.row-status-refusal { background: #ede0ff; --details-card-bg: #dac9f0; }
.row-status-unknown { background: #f1f3f5; --details-card-bg: #e0e4e8; }
.row-cat-top { background: #fff3cd; --details-card-bg: #ead99c; }
.row-cat-key { background: #cce5ff; --details-card-bg: #b4d4f5; }
.row-cat-regular { background: #f8f9fa; --details-card-bg: #e9ecef; }
.row-close-early { background: #d4edda; --details-card-bg: #bfe2c8; }
.row-close-lastday { background: #ffe4b5; --details-card-bg: #f5d9a0; }
.row-close-late { background: #ffe8e8; --details-card-bg: #f8d5d5; }
.row-close-neutral { background: #f1f3f5; --details-card-bg: #e0e4e8; }

.refresh-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  overflow: hidden;
  background: transparent;
}

.refresh-bar::after {
  content: '';
  display: block;
  width: 35%;
  height: 100%;
  background: #007bff;
  animation: refresh-slide 1s linear infinite;
}

@keyframes refresh-slide {
  from { transform: translateX(-100%); }
  to { transform: translateX(300%); }
}

/* Виджет «Закрытие» (портирован из статистики, TripsTab) */
.tl-widget {
  position: relative;
  width: 120px;
  height: 14px;
}

.tl-track {
  width: 100%;
  height: 100%;
  border-radius: 3px;
  background: #f2f4f7;
  overflow: hidden;
}

.tl-bar {
  height: 100%;
  border-radius: 3px 0 0 3px;
}

.tl-early { background: #12b76a; color: #12b76a; }
.tl-lastday { background: #f79009; color: #f79009; }
.tl-late { background: #f04438; color: #f04438; }
.tl-neutral { background: #98a2b3; color: #98a2b3; }

.tl-open {
  opacity: .55;
  background: repeating-linear-gradient(45deg, currentColor 0 3px, transparent 3px 6px);
}

.tl-mark {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 1px;
  background: #98a2b3;
}
</style>
